import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { OpenapiQueryClient } from 'openapi-react-query';
import { createContext, useContext } from 'react';
import type { paths } from '../__generated__/openapi';
import { FlikHttpAuthOptions } from '../auth';
import { ExponentialBackoffOptions } from '../retries';
import { createReactQueryClient } from './client';

interface ReactQueryProviderProps {
  uri: () => string;
  auth: FlikHttpAuthOptions;
  backoff?: ExponentialBackoffOptions;
  children: React.ReactNode;
  enableDevtools?: boolean;
}

// Create a context for the API client
const ReactQueryContext = createContext<OpenapiQueryClient<paths> | null>(null);

/**
 * Provider component for React Query with automatic authentication refresh
 */
export const ReactQueryProvider = ({
  uri,
  auth,
  backoff,
  children,
  enableDevtools = false,
}: ReactQueryProviderProps) => {
  // Create the React Query client with auth refresh functionality
  const { openapiClient, queryClient } = createReactQueryClient({
    uri,
    auth,
    backoff,
  });

  return (
    <ReactQueryContext.Provider value={openapiClient}>
      <QueryClientProvider client={queryClient}>
        {children}
        {enableDevtools && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </ReactQueryContext.Provider>
  );
};

/**
 * Hook to access the React Query client with OpenAPI-generated hooks
 */
export const useReactQuery = () => {
  const context = useContext(ReactQueryContext);
  if (!context) {
    throw new Error('useReactQuery must be used within a ReactQueryProvider');
  }
  return context;
};
